import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import { alpha, Box, Card as MuiCard, Typography } from "@mui/material";
import { MatchData, MatchStatus } from "@virtualprogaming/js-public-sdk";
import React from "react";
import { useHistory } from "react-router-dom";

import IconShoe from "~/images/icons/icon-shoe.svg";
import { useUser } from "~/queries";
import { Icon, ImageVariant, UserAvatar } from "~/ui/components";
import { appTheme } from "~/ui/theme";
import { useScreenSm } from "~/utils";

export interface LineupPlayerCardProps {
  player: MatchData;
  mvp?: MatchData | null | undefined;
  matchStatus?: MatchStatus;
}

// Style constants
const CARD_STYLES = {
  main: {
    height: "80px",
    p: 1.5,
    background: `linear-gradient(135deg, ${alpha("#222", 0.9)}, ${alpha(
      "#111",
      0.9
    )})`,
    backdropFilter: "blur(5px)",
    border: `1px solid ${alpha(appTheme.palette.primary.main, 0.1)}`,
    borderRadius: 2,
    boxShadow: `0 4px 12px ${alpha("#000", 0.2)}`,
    position: "relative",
    overflow: "hidden",
    mb: 1.5,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, ${alpha(
        appTheme.palette.primary.main,
        0.3
      )}, ${alpha(appTheme.palette.primary.main, 0.5)}, ${alpha(
        appTheme.palette.primary.main,
        0.3
      )})`,
    },
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 6px 16px ${alpha("#000", 0.3)}`,
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "100%",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  leftSection: (isMobile: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 1 : 3,
    minWidth: 0, // Prevent overflow issues
  }),
  clickableArea: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    mr: 2,
  },
  avatar: {
    width: "35px",
  },
  username: (isMobile: boolean) => ({
    ml: 1.5,
    flex: 1,
    maxWidth: isMobile ? "110px" : "150px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#FFFFFF",
    fontWeight: 500,
  }),
  statsContainer: (isMobile: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    // Indent stats to align with username on mobile
    paddingLeft: isMobile ? "47px" : 0,
    marginTop: isMobile ? 0.5 : 0,
    ...(isMobile
      ? {
          "& > div": {
            gap: 0.5,
            "& svg, & img": {
              fontSize: "0.85rem",
              height: "16px !important",
            },
          },
        }
      : {
          "& > div": {
            gap: 1,
          },
        }),
  }),
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    color: appTheme.palette.primary.main,
  },
  mvpIcon: {
    display: "flex",
    alignItems: "center",
    height: "30px",
    mr: 0.5,
    color: appTheme.palette.primary.main,
  },
  positionBadge: {
    backgroundColor: alpha("#000", 0.6),
    color: appTheme.palette.primary.main,
    px: 1.5,
    py: 0.5,
    borderRadius: 1,
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    border: `1px solid ${alpha(appTheme.palette.primary.main, 0.3)}`,
    alignSelf: "center",
  },
};

// Fallback chain prioritizes external platform identities over generic usernames
const getDisplayName = (
  infos: { data?: { origin?: string; psn?: string; xbox?: string } },
  username: string
): string => {
  return (
    infos?.data?.origin || infos?.data?.psn || infos?.data?.xbox || username
  );
};

// MVP determination requires both positive rating and username match to prevent false positives
const checkIsMvp = (
  mvp: MatchData | null | undefined,
  username: string
): boolean => {
  return mvp ? mvp.match_rating > 0 && mvp.username === username : false;
};

// Stats visibility tied to match completion prevents showing incomplete data
const shouldShowStats = (matchStatus?: MatchStatus): boolean => {
  return matchStatus === MatchStatus.Complete;
};

// Sub-components
interface StatIconsProps {
  count: number;
  renderIcon: (index: number) => React.ReactNode;
}

// Caps visual icon display at 5 to prevent UI overflow while preserving count accuracy
const StatIcons: React.FC<StatIconsProps> = ({ count, renderIcon }) => {
  const displayCount = count > 5 ? 5 : count;

  return (
    <Box sx={CARD_STYLES.statItem}>
      {Array.from(Array(displayCount).keys()).map(renderIcon)}
      {count > 5 && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          +
        </Typography>
      )}
    </Box>
  );
};

interface PlayerAvatarSectionProps {
  avatar: string | undefined;
  displayName: string;
  isMobile: boolean;
  onUserClick: () => void;
}

const PlayerAvatarSection: React.FC<PlayerAvatarSectionProps> = ({
  avatar,
  displayName,
  isMobile,
  onUserClick,
}) => (
  <Box onClick={onUserClick} sx={CARD_STYLES.clickableArea}>
    <Box sx={CARD_STYLES.avatar}>
      <UserAvatar avatar={avatar} variant={ImageVariant.SmallThumbnail} />
    </Box>
    <Typography sx={CARD_STYLES.username(isMobile)}>{displayName}</Typography>
  </Box>
);

interface MatchStatsProps {
  isMvp: boolean;
  goals: number;
  assists: number;
  saves: number;
  isMobile: boolean;
}

// Conditional rendering based on stat values prevents empty icon groups
const MatchStats: React.FC<MatchStatsProps> = ({
  isMvp,
  goals,
  assists,
  saves,
  isMobile,
}) => (
  <Box sx={CARD_STYLES.statsContainer(isMobile)}>
    {isMvp && (
      <Box sx={CARD_STYLES.mvpIcon}>
        <EmojiEventsIcon fontSize="small" />
      </Box>
    )}

    {goals > 0 && (
      <StatIcons
        count={goals}
        renderIcon={(index) => (
          <SportsSoccerIcon key={index} fontSize="small" />
        )}
      />
    )}

    {assists > 0 && (
      <StatIcons
        count={assists}
        renderIcon={(index) => (
          <img src={IconShoe} key={index} alt="assist" style={{ height: 20 }} />
        )}
      />
    )}

    {saves > 0 && (
      <StatIcons
        count={saves}
        renderIcon={(index) => (
          <Icon key={index} icon={["far", "mitten"]} size="sm" />
        )}
      />
    )}
  </Box>
);

export const LineupPlayerCard: React.FC<LineupPlayerCardProps> = ({
  player,
  mvp,
  matchStatus,
}) => {
  const { username, position, avatar, goals, saves, assists } = player;
  const { push } = useHistory();
  const infos = useUser(username);
  const isMobile = useScreenSm();
  const displayName = getDisplayName(infos, username);
  const isMvp = checkIsMvp(mvp, username);
  const showStats = shouldShowStats(matchStatus);

  const handleUserClick = () => push(`/user/${username}`);

  return (
    <MuiCard sx={CARD_STYLES.main}>
      <Box sx={CARD_STYLES.container}>
        <Box sx={CARD_STYLES.topRow}>
          <Box sx={CARD_STYLES.leftSection(isMobile)}>
            <PlayerAvatarSection
              avatar={avatar}
              displayName={displayName}
              isMobile={isMobile}
              onUserClick={handleUserClick}
            />
          </Box>
          <Box sx={CARD_STYLES.positionBadge}>{position}</Box>
        </Box>

        {showStats && (
          <MatchStats
            isMvp={isMvp}
            goals={goals}
            assists={assists}
            saves={saves}
            isMobile={isMobile}
          />
        )}
      </Box>
    </MuiCard>
  );
};
